/**
 * Shared utility types and functions used by entity renderers in
 * PrimitiveAtlasStage and its sub-modules.
 *
 * Extracted from PrimitiveAtlasStage.tsx — pure structural refactor.
 */

export type StagePoint = {
  x: number;
  y: number;
};

export function readNumber(
  properties: Record<string, string>,
  key: string,
  fallback = 0,
) {
  const parsed = Number(properties[key]);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function readPositiveNumber(
  properties: Record<string, string>,
  key: string,
  fallback: number,
) {
  const parsed = Number(properties[key]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function readPointList(value: string | undefined): StagePoint[] {
  if (!value) {
    return [];
  }

  return value
    .split(";")
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => pair.split(","))
    .map(([x, y]) => ({
      x: Number(x),
      y: Number(y),
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
}

export function polarPoint(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

export function describeArc(
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number,
) {
  const start = polarPoint(centerX, centerY, radius, endAngle);
  const end = polarPoint(centerX, centerY, radius, startAngle);
  const span = (((endAngle - startAngle) % 360) + 360) % 360;
  const largeArc = span > 180 ? 1 : 0;

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Rotate the relative offset `(dx, dy)` by `angleDeg` (AutoCAD CCW
 * convention) and add it to `(baseX, baseY)`. One-truth home for the
 * "grip at base + rotated offset" math shared by the Rectangular
 * Associative Array grip builders — the legacy 2D `gripCalculation.ts`
 * (canonical count/spacing grips) and the scene3d `arrayGrips.ts`
 * (row/column insertion chevrons) both position every non-origin array
 * grip this way. Extracted (jscpd DRY gate) rather than hand-rolled
 * `cos`/`sin` in each call site.
 */
export function rotateOffsetAroundBaseDeg(
  baseX: number,
  baseY: number,
  dx: number,
  dy: number,
  angleDeg: number,
): StagePoint {
  const cosA = Math.cos((angleDeg * Math.PI) / 180);
  const sinA = Math.sin((angleDeg * Math.PI) / 180);
  return {
    x: baseX + dx * cosA - dy * sinA,
    y: baseY + dx * sinA + dy * cosA,
  };
}

/**
 * S-ARRAYRECT-AXISANGLE-UNIT — read the Rectangular Associative Array "Axis
 * angle" property. As of the Codex P2 (local review round 4) write-boundary
 * fix, this is a plain passthrough: `buildArrayRectPreview`
 * (apps/web/src/app/state/dxfGeometry.ts) ALREADY projects the bag's "Axis
 * angle" string in DEGREES — unconditionally, for both associative and
 * non-associative arrays — mirrored 1:1 by the typed `arrayRectDescriptors`
 * "Axis angle" descriptor's `display`/`toRaw`
 * (packages/command-engine/src/descriptors/containers.ts), which converts a
 * typed degree edit back into the entity's native storage unit (RADIANS for
 * an associative array's resolved AcDbAssocArrayRectangularParameters
 * "AxesAngle", byte-matched against real AutoCAD files — DEGREES straight
 * off DXF group 50 for a non-associative array). So by the time ANY consumer
 * — this helper's callers (the scene3d render/bounds adapter, the
 * insertion-chevron grips, the canonical count/spacing grips, grip-drag) —
 * reads "Axis angle", it is always already degrees; no per-consumer unit
 * resolution is needed here.
 *
 * PRIOR HISTORY (kept for context — do not resurrect this class of fix here):
 * rounds 1-3 tried to resolve the unit AT READ TIME, first via a blanket
 * "Associative=Yes -> always radians" rule (broke a typed "90" edit —
 * 90 * 180/π ≈ 5158°), then via a `|raw| <= 2*PI` magnitude range guard
 * (narrowed but could not close the gap — a typed "5" edit is ALSO
 * `<= 2*PI` in magnitude, indistinguishable from a genuine small imported
 * radians value by magnitude alone). Round 4 moved the conversion to the
 * WRITE boundary instead (the typed descriptor above) — the entity's
 * `axisAngle` field is now unconditionally radians-for-associative
 * regardless of provenance (import OR edit), which is what lets every
 * reader, including this one, drop the heuristic entirely.
 */
export function resolveArrayAxisAngleDeg(
  properties: Record<string, string>,
): number {
  return readNumber(properties, "Axis angle", 0);
}

export function pointFrom(
  properties: Record<string, string>,
  xKey: string,
  yKey: string,
): StagePoint {
  return {
    x: readNumber(properties, xKey),
    y: readNumber(properties, yKey),
  };
}

/**
 * Sanitize an arbitrary string into a token safe for use inside a
 * `data-testid` attribute. Preserves byte-identical behaviour with the
 * original module-private helper that lived in PrimitiveAtlasStage.tsx
 * before the PR-0 split:
 *   - lowercase first
 *   - non-alphanumeric runs collapsed to a single `-`
 *
 * Note: This intentionally differs from `apps/web/src/app/helpers/formatHelpers.ts:toTestIdSegment`,
 * which also strips leading/trailing dashes. Stage entity test ids must
 * keep the original behaviour so existing E2E selectors keep matching.
 */
export function toTestIdSegment(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}
